import type { RoleMembre } from './types'

/**
 * Ce que chaque rôle PEUT RÉELLEMENT faire — et rien d'autre.
 *
 * ⚠️ La version HTML proposait des « rôles personnalisés » (nom, description,
 * cases à cocher) et des droits ajustables par membre. Rien de tout cela
 * n'était appliqué : les rôles sur mesure vivaient dans un tableau en mémoire
 * qui disparaissait au rechargement, et les cases cochées étaient écrites dans
 * `memberships.permissions`, une colonne qu'AUCUNE policy RLS ni AUCUNE edge
 * function ne lit. On pouvait « retirer » la comptabilité à quelqu'un qui
 * continuait d'y avoir accès.
 *
 * La seule chose qui décide est `memberships.role` (énumération figée à quatre
 * valeurs, 0001). Ce tableau la DÉCRIT, d'après les policies et les edge
 * functions — il ne prétend pas l'ajuster. La console s'en sert aussi pour ne
 * pas proposer un bouton que la base refuserait.
 */

export type Capacite =
  | 'voir_exploitation'
  | 'courses'
  | 'clients'
  | 'livreurs'
  | 'messagerie'
  | 'comptabilite'
  | 'factures'
  | 'depenses'
  | 'ecritures'
  | 'cloture'
  | 'parametres_org'
  | 'equipe'
  | 'boitiers'
  | 'boitiers_voir'
  | 'alertes_voir'
  | 'alertes_gerer'
  | 'moteur_boitiers'
  | 'tarifs'
  | 'forfaits'
  | 'abonnements'
  | 'abonnements_devis'
  | 'encaissements'
  | 'annuler_encaissements'
  | 'onboarding_voir'
  | 'onboarding_gerer'
  | 'onboarding_proprietaire'
  | 'paie_livreurs'
  | 'app_livreur'

export interface DefinitionCapacite {
  cle: Capacite
  libelle: string
  roles: readonly RoleMembre[]
  /** D'où vient la règle — pour qu'on puisse la vérifier, pas la croire. */
  source: string
}

export const CAPACITES: readonly DefinitionCapacite[] = [
  {
    cle: 'voir_exploitation',
    libelle: 'Voir les courses, les clients et les livreurs',
    roles: ['owner', 'dispatcher', 'accountant'],
    source: 'courses_staff_read (0013), client_accounts_member_read, drivers_member_read',
  },
  {
    cle: 'courses',
    libelle: 'Créer, affecter, faire avancer et annuler une course',
    roles: ['owner', 'dispatcher'],
    source: 'courses_dispatch (0001) · edge functions assign-course, set-course-status',
  },
  {
    cle: 'clients',
    libelle: 'Créer et modifier les fiches clients',
    roles: ['owner', 'dispatcher'],
    source: 'client_accounts_manage, client_slots_manage (0002)',
  },
  {
    cle: 'livreurs',
    libelle: 'Créer les comptes livreurs',
    roles: ['owner', 'dispatcher'],
    source: 'drivers_manage (0001) · edge function create-driver-account',
  },
  {
    cle: 'messagerie',
    libelle: 'Lire et écrire aux livreurs',
    roles: ['owner', 'dispatcher'],
    source: 'dm_staff_read, dm_staff_send (0005)',
  },
  {
    cle: 'comptabilite',
    libelle: 'Consulter la comptabilité (journal, balance, résultat, tiers)',
    roles: ['owner', 'accountant'],
    source: 'invoices_read, invoice_lines_read (0003) — sans les factures, les états seraient faux',
  },
  {
    cle: 'factures',
    libelle: 'Émettre des factures',
    roles: ['owner', 'accountant'],
    source: 'invoices_manage, invoice_lines_manage (0003) · generer_factures_mois, regler_facture (0032 / 0033)',
  },
  {
    cle: 'depenses',
    libelle: 'Saisir et supprimer des dépenses',
    roles: ['owner', 'accountant'],
    source: 'expenses_manage (0001) · edge function record-expense',
  },
  {
    cle: 'ecritures',
    libelle: 'Écritures manuelles et plan de comptes',
    roles: ['owner', 'accountant'],
    source: 'org_documents (0008) — la base l’ouvre aussi au dispatch, la console le réserve à la compta',
  },
  {
    cle: 'cloture',
    libelle: 'Clôturer ou rouvrir un exercice',
    roles: ['owner', 'accountant'],
    source: 'accounting_closures_insert, accounting_closure_events_insert (0008)',
  },
  {
    cle: 'parametres_org',
    libelle: 'Modifier TVA, identité fiscale, préfixe de facture, preuve de livraison',
    roles: ['owner'],
    source: 'org_owner_maj (0016)',
  },
  {
    cle: 'equipe',
    libelle: 'Inviter, changer le rôle, suspendre un membre',
    roles: ['owner'],
    source: 'edge functions invite-member, set-member-role',
  },
  {
    cle: 'boitiers',
    libelle: 'Gérer les boîtiers GPS (nom, rattachement), le secret de connexion à Traccar et leur envoyer des commandes',
    roles: ['owner', 'dispatcher'],
    source: 'gps_devices_ajout / maj / suppression, regenerer_secret_gps (0026) · gps_commande_autoriser : owner et dispatcher (0052)',
  },
  {
    cle: 'boitiers_voir',
    libelle: 'Voir les boîtiers GPS et leur position',
    roles: ['owner', 'dispatcher', 'accountant'],
    source: 'gps_devices_lecture : is_org_staff (0049)',
  },
  {
    cle: 'alertes_voir',
    libelle: 'Consulter les alertes GPS, les règles, les zones et les réglages de notification',
    roles: ['owner', 'dispatcher', 'accountant'],
    source: 'gps_alertes / gps_alertes_regles / gps_zones / gps_alertes_reglages : lecture is_org_staff ; gps_alertes_compte (0052)',
  },
  {
    cle: 'alertes_gerer',
    libelle: 'Régler les règles, dessiner les zones, brancher les notifications, acquitter les alertes',
    roles: ['owner', 'dispatcher'],
    source: 'écriture has_org_role(owner, dispatcher) sur les mêmes tables ; gps_alertes_acquitter (0052)',
  },
  {
    cle: 'moteur_boitiers',
    libelle: 'Couper ou rétablir le moteur d’un véhicule à boîtier',
    roles: ['owner'],
    source: 'gps_commande_autoriser : « role_admin_requis » pour tout autre rôle (0052) — refusée aussi si le véhicule roule',
  },
  {
    cle: 'tarifs',
    libelle: 'Modifier la grille tarifaire opérée et les frais mobile money',
    roles: ['owner'],
    source: 'tarifs_operes_owner_update (0030) — le prix des courses opérées vient de cette grille, verrouillé par la base',
  },
  {
    cle: 'forfaits',
    libelle: 'Modifier les forfaits PME (libellé, prix mensuel, quota, actif)',
    roles: ['owner'],
    source: 'forfaits_operes_owner_update (0031) — les abonnements existants gardent le prix et le quota figés à leur souscription',
  },
  {
    cle: 'abonnements',
    libelle: 'Souscrire un client PME à un forfait de la grille, changer son forfait (au 1er du mois suivant)',
    roles: ['owner', 'dispatcher', 'accountant'],
    source: 'abonner_client, changer_forfait_client (0031) — le dispatch, qui onboarde les PME, ne peut choisir qu’un forfait AU PRIX DE LA GRILLE',
  },
  {
    cle: 'abonnements_devis',
    libelle: 'Fixer un prix et un quota négociés (forfait sur devis)',
    roles: ['owner', 'accountant'],
    source: 'abonner_client, changer_forfait_client (0031) — devis_reserve_au_proprietaire pour le dispatch',
  },
  {
    cle: 'encaissements',
    libelle: 'Enregistrer l’encaissement d’une course de particulier (Wave, Orange Money, espèces, autre)',
    roles: ['owner', 'dispatcher', 'accountant'],
    source: 'enregistrer_encaissement_course (0033) — borné par le restant dû, journalisé avec son auteur',
  },
  {
    cle: 'annuler_encaissements',
    libelle: 'Annuler un encaissement enregistré par erreur',
    roles: ['owner', 'accountant'],
    source: 'annuler_encaissement_course (0033)',
  },
  {
    cle: 'onboarding_voir',
    libelle: 'Consulter l’onboarding des clients PME (dossiers, pièces, contrat, journal)',
    roles: ['owner', 'dispatcher', 'accountant'],
    source: 'client_documents / contract_signatures / onboarding_events : lecture owner, dispatcher, comptable (0034)',
  },
  {
    cle: 'onboarding_gerer',
    libelle: 'Instruire un dossier PME : pièces, offre, envoi du contrat, refus et suspension',
    roles: ['owner', 'dispatcher'],
    source: 'cdocs_depot / cdocs_maj (0034), client_accounts_manage (0002), edge function envoyer-contrat-signature',
  },
  {
    cle: 'onboarding_proprietaire',
    libelle: 'Activer un client PME, acceptation manuelle du contrat, publier les modèles de contrat',
    roles: ['owner'],
    source: 'activer_client_pme, accepter_contrat_manuellement, contrat_owner_* (0034)',
  },
  {
    cle: 'paie_livreurs',
    libelle: 'Voir et payer les primes de forte demande des livreurs',
    roles: ['owner', 'accountant'],
    source: 'paie_livreurs_mois, marquer_primes_payees, paies_primes_lecture (0044)',
  },
  {
    cle: 'app_livreur',
    libelle: 'App Livreur : ses courses, sa position, ses messages — jamais cette console',
    roles: ['driver'],
    source: 'courses_driver_read (0013), drivers_self_update (0017)',
  },
]

export const ROLES: readonly RoleMembre[] = ['owner', 'dispatcher', 'accountant', 'driver']

export const LIBELLE_ROLE: Record<RoleMembre, string> = {
  owner: 'Propriétaire',
  dispatcher: 'Dispatch',
  accountant: 'Comptable',
  driver: 'Livreur',
}

export const DESCRIPTION_ROLE: Record<RoleMembre, string> = {
  owner: 'Tout, y compris l’équipe et les réglages de l’organisation.',
  dispatcher: 'L’exploitation : appels, courses, clients, livreurs, messagerie, encaissement des courses. Pas la comptabilité.',
  accountant: 'La comptabilité : factures, dépenses, écritures, clôture. Consulte l’exploitation sans la modifier.',
  driver: 'L’app Livreur uniquement. Refusé dans cette console.',
}

/** Le rôle a-t-il cette capacité ? Un rôle inconnu n'a rien. */
export function peut(role: RoleMembre | null | undefined, cle: Capacite): boolean {
  if (!role) return false
  return CAPACITES.some((c) => c.cle === cle && c.roles.includes(role))
}

/** Phrase de refus, pour expliquer un bouton désactivé plutôt que le cacher sans mot. */
export function refus(role: RoleMembre | null | undefined): string {
  return role ? `Votre rôle (${LIBELLE_ROLE[role]}) ne le permet pas.` : 'Rôle inconnu : action indisponible.'
}
